import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Boxes, ClipboardList, FlaskConical, Hourglass, Package, Settings, ShoppingCart, Snowflake } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { MasterDataTab } from '@/components/inventory/MasterDataTab'
import { KitsTab } from '@/components/inventory/KitsTab'
import { PendingInventoryTab } from '@/components/inventory/PendingInventoryTab'
import { MasterConfigTab } from '@/components/inventory/MasterConfigTab'
import { ReorderTab } from '@/components/inventory/ReorderTab'
import { StorageTab } from '@/components/inventory/storage/StorageTab'
import { MyListTab } from '@/components/equipment/mylist/MyListTab'
import { EquipmentScopeProvider, STORAGE_SCOPE } from '@/components/equipment/equipmentScope'
import { OverallStatusLink } from '@/components/board/OverallStatusLink'
import { useReorderSummary } from '@/hooks/useInventoryMaster'
import { useTabAccess } from '@/hooks/useTabAccess'
import { inventoryApi } from '@/api/inventoryApi'
import { useTenant } from '@/providers/TenantProvider'
import { INVENTORY_TABS, inventoryTabAccess, type InventoryTab } from '@/lib/pageTabs'

// The rebuilt inventory module (epic 86eydpruy): the profile-driven material
// master, surfaced as per-type tabs (Consumables / Reagents / Kits) that filter
// the same materials table, plus Master Data Config (last) for the field
// dictionary + shared presets. Legacy mock tabs are retired here.
//
// Ids, labels and order live in src/lib/pageTabs.ts, shared with the main menu
// (z8nrz7f7g3); the icon and the material-type filter are this page's own.
const TAB_EXTRAS: Record<InventoryTab, { icon: React.ElementType; materialType?: string }> = {
  consumables: { icon: Package, materialType: 'Consumable' },
  reagents: { icon: FlaskConical, materialType: 'Reagent' },
  kits: { icon: Boxes, materialType: 'Kit' },
  pending: { icon: Hourglass },
  storage: { icon: Snowflake },
  tasks: { icon: ClipboardList },
  reorder: { icon: ShoppingCart },
  'master-config': { icon: Settings },
}

const TAB_DEFS = INVENTORY_TABS.map((t) => ({ ...t, ...TAB_EXTRAS[t.value] }))

/** Tabs that are not a filter over the materials table. */
const NON_MATERIAL_TABS = new Set<InventoryTab>(['kits', 'pending', 'storage', 'tasks', 'reorder', 'master-config'])

const VALID_TABS = new Set<string>(TAB_DEFS.map((t) => t.value))
const DEFAULT_TAB: InventoryTab = 'consumables'

export function InventoryPage() {
  const [params, setParams] = useSearchParams()
  const param = params.get('tab')
  // Tasks has a gate of its own; the rest open with the page (src/lib/pageTabs.ts).
  const allowed = inventoryTabAccess(useTabAccess())
  const requested: InventoryTab =
    param && VALID_TABS.has(param) ? (param as InventoryTab) : DEFAULT_TAB
  const tab: InventoryTab = allowed[requested] ? requested : DEFAULT_TAB
  // What needs ordering, counted on the tab itself so it is seen from any tab.
  // Left off while loading (or on a backend without the route) rather than
  // showing a 0 that is not known to be true.
  const attention = useReorderSummary().data?.attention

  function setTab(next: string) {
    const sp = new URLSearchParams(params)
    sp.set('tab', next)
    setParams(sp, { replace: true })
  }

  // `?material=<id>` (an Action Item's Source, z8nrz7fkfh): the material's own
  // tab, its table searched to it. There is no page for one material.
  const { selectedTenantId } = useTenant()
  const materialParam = params.get('material')
  useEffect(() => {
    if (!materialParam || !selectedTenantId) return
    let cancelled = false
    inventoryApi
      .getMaterial(selectedTenantId, materialParam)
      .then((m) => {
        if (cancelled) return
        const home = TAB_DEFS.find((t) => !NON_MATERIAL_TABS.has(t.value) && t.materialType === m.materialType)
        setParams({ tab: home?.value ?? DEFAULT_TAB, q: m.materialId }, { replace: true })
      })
      .catch(() => {
        if (!cancelled) setParams({}, { replace: true })
      })
    return () => {
      cancelled = true
    }
  }, [materialParam, selectedTenantId, setParams])

  return (
    <div className='min-h-full bg-muted/20'>
      <div className='w-full px-6 py-8'>
        <div className='mb-5 flex items-center gap-3'>
          <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary'>
            <Package className='h-5 w-5 text-primary-foreground' />
          </div>
          <h1 className='text-2xl font-semibold tracking-tight text-gray-900'>Inventory</h1>
          {/* Receiving runs on the Overall Status board. */}
          <OverallStatusLink type='Inventory' placement='title' />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {TAB_DEFS.filter((t) => allowed[t.value]).map(({ value, label, icon: Icon }) => {
              const count = value === 'reorder' ? attention : undefined
              return (
                <TabsTrigger key={value} value={value} className='gap-2'>
                  <Icon className='h-4 w-4' />
                  {count != null ? `${label} (${count})` : label}
                </TabsTrigger>
              )
            })}
          </TabsList>

          {TAB_DEFS.filter((t) => !NON_MATERIAL_TABS.has(t.value)).map(({ value, materialType }) => (
            <TabsContent key={value} value={value} className='mt-4'>
              <MasterDataTab materialType={materialType} />
            </TabsContent>
          ))}
          {/* Kits is not a material-type filter: a kit is a FORM, so this tab
              shows kit builds and completed kits, not rows of Material. */}
          <TabsContent value='kits' className='mt-4'>
            <KitsTab />
          </TabsContent>
          <TabsContent value='pending' className='mt-4'>
            <PendingInventoryTab />
          </TabsContent>
          <TabsContent value='storage' className='mt-4'>
            <StorageTab />
          </TabsContent>
          {/* Storage units' tasks (z8nrz7fjzc): the Cleaning / Equipment
              Tasks tab, scoped to Purpose=Storage. */}
          {allowed.tasks && (
            <TabsContent value='tasks' className='mt-4'>
              <EquipmentScopeProvider scope={STORAGE_SCOPE}>
                <MyListTab />
              </EquipmentScopeProvider>
            </TabsContent>
          )}
          <TabsContent value='reorder' className='mt-4'>
            <ReorderTab />
          </TabsContent>
          <TabsContent value='master-config' className='mt-4'>
            <MasterConfigTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
